// Read-only "preview as user" view of a Composer template.
// Renders intro_message, wizard_questions, and default_structure exactly the
// way an end-user sees them after picking the template — admins use this to
// QA Excerptus catalog entries; Composer users use it to preview before clone.
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  contentTypeLabel,
  parseStructure,
  parseWizardQuestions,
} from "@/lib/composerTemplates";
import { templateToBriefDocument } from "@/lib/composerPushDocs";
import { renderBriefDocument } from "@/lib/briefDocument";

type TemplatePreviewProps = {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  name: string;
  contentType: string;
  description?: string | null;
  introMessage?: string | null;
  wizardQuestions?: unknown;
  defaultStructure?: unknown;
  /** Reason buttons are inert (e.g. "Preview only"). When set, all CTAs are disabled. */
  readOnlyReason?: string;
  onUse?: () => void;
  onClone?: () => void;
};

export const TemplatePreviewDialog = ({
  open,
  onOpenChange,
  name,
  contentType,
  description,
  introMessage,
  wizardQuestions,
  defaultStructure,
  readOnlyReason,
  onUse,
  onClone,
}: TemplatePreviewProps) => {
  const questions = parseWizardQuestions(wizardQuestions as never);
  const sections = parseStructure(defaultStructure as never);
  const doc = templateToBriefDocument({
    introMessage: introMessage ?? null,
    defaultStructure,
  });
  const rendered = renderBriefDocument(doc, {
    // Static template preview — canonical CSL id from csl_styles_catalog.
    style: "chicago-notes-bibliography",
    mode: "footnotes",
    references: [],
    hideAutoBibliography: true,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {name}
            <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
              {contentTypeLabel(contentType)}
            </Badge>
          </DialogTitle>
          {description ? (
            <DialogDescription>{description}</DialogDescription>
          ) : (
            <DialogDescription>Preview — exactly as a user sees this template.</DialogDescription>
          )}
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-3">
          <div className="space-y-5">
            {introMessage?.trim() && (
              <section>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Intro message
                </h4>
                <p className="rounded-md border border-border/60 bg-muted/30 p-3 text-sm italic text-muted-foreground">
                  {introMessage}
                </p>
              </section>
            )}

            {questions.length > 0 && (
              <section>
                <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  Wizard questions ({questions.length})
                </h4>
                <ol className="list-decimal space-y-1.5 pl-5 text-sm">
                  {questions.map((q) => (
                    <li key={q.id}>
                      <span className="font-medium">{q.prompt}</span>
                      {q.required && <span className="ml-1 text-destructive">*</span>}
                      {q.hint && (
                        <p className="text-xs text-muted-foreground">{q.hint}</p>
                      )}
                    </li>
                  ))}
                </ol>
              </section>
            )}

            <section>
              <h4 className="mb-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Default structure ({sections.length} {sections.length === 1 ? "section" : "sections"})
              </h4>
              {rendered.html ? (
                <div
                  className="prose prose-sm dark:prose-invert max-w-none rounded-md border border-border/60 bg-muted/20 p-3"
                  dangerouslySetInnerHTML={{ __html: rendered.html }}
                />
              ) : (
                <p className="text-xs text-muted-foreground italic">
                  No default structure — the canvas opens blank.
                </p>
              )}
            </section>
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2">
          {readOnlyReason && (
            <span className="mr-auto text-xs text-muted-foreground italic">{readOnlyReason}</span>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {onClone && (
            <Button
              variant="outline"
              onClick={() => {
                onClone();
                onOpenChange(false);
              }}
              disabled={Boolean(readOnlyReason)}
              title={readOnlyReason}
            >
              Save a copy
            </Button>
          )}
          {onUse && (
            <Button
              onClick={() => {
                onUse();
                onOpenChange(false);
              }}
              disabled={Boolean(readOnlyReason)}
              title={readOnlyReason}
            >
              Use template
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TemplatePreviewDialog;
